import React from 'react';
import { Box, Stack, Button, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {
  buttonStyles,
  dividerStyles,
} from '../../../modals/unified_config_modals/styles/unifiedConfigStyles';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import SensitivityChip from '../../../shared/components/SensitivityChip';

const TOTAL_CHIPS = 11;

interface Props {
  values: boolean[];
  onToggle: (index: number) => void;
  smartAdaptive: boolean;
  setSmartAdaptive: (v: boolean) => void;
  onEnableAll: () => void;
  onDisableAll: () => void;
  disabled?: boolean;
  readonly?: boolean;
  onChipHover?: (sensitivity: number) => void;
  onChipLeave?: () => void;
}

const Flat11SensitivityField: React.FC<Props> = ({
  values,
  onToggle,
  smartAdaptive,
  setSmartAdaptive,
  onEnableAll,
  onDisableAll,
  disabled = false,
  readonly = false,
  onChipHover,
  onChipLeave,
}) => {
  const { t } = useTranslation();

  if (values.length !== TOTAL_CHIPS) {
    console.error(
      `Flat11SensitivityField expected ${TOTAL_CHIPS} chips, got ${values.length}`,
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
        {values.map((selected, i) => (
          <SensitivityChip
            key={i}
            label={i + 1}
            selected={selected}
            disabled={disabled}
            readonly={readonly}
            onClick={() => onToggle(i)}
            onMouseEnter={() => onChipHover?.(i + 1)}
            onMouseLeave={() => onChipLeave?.()}
          />
        ))}

        <Box sx={dividerStyles.vertical(disabled || readonly)} />

        <SensitivityChip
          label={t(StringKeys.ADAPTIVE_MODES)}
          selected={smartAdaptive}
          variant="smart-adaptive"
          disabled={disabled}
          readonly={readonly}
          onClick={() => setSmartAdaptive(!smartAdaptive)}
        />
      </Stack>

      {!readonly && (
        <Stack direction="row" spacing={1} mt={2}>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              if (!disabled) {
                onEnableAll();
                setSmartAdaptive(true);
              }
            }}
            sx={buttonStyles.enableAll}
          >
            {t(StringKeys.ENABLE_ALL)}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              if (!disabled) {
                onDisableAll();
                setSmartAdaptive(false);
              }
            }}
            sx={buttonStyles.disableAll}
          >
            {t(StringKeys.DISABLE_ALL)}
          </Button>
        </Stack>
      )}

      {!readonly && !smartAdaptive && (
        <Stack direction="row" alignItems="center" spacing={0.5} mt={1.5}>
          <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 16 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t(StringKeys.ENABLE_SMART_ADAPTIVE_FOR_ADVANCED_SETTINGS)}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

export default Flat11SensitivityField;
